import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getLang } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const GOOGLE_REVIEW_URL = 'https://g.page/r/CYSQIA_qXafTEBM/review';
const TRUSTPILOT_REVIEW_URL = 'https://de.trustpilot.com/evaluate/yourgermanaddress.com';
const ui = (de, en) => (getLang() === 'en' ? en : de);

function normalizeRating(value) {
  const rating = Number(value);
  return Number.isInteger(rating) && rating >= 1 && rating <= 5 ? rating : null;
}

function Feedback() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [rating, setRating] = useState(normalizeRating(searchParams.get('rating')) || 0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const starButtons = useMemo(() => [1, 2, 3, 4, 5], []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating) {
      setError(ui('Bitte wählen Sie eine Bewertung aus.', 'Please select a rating.'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, message }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || ui('Feedback konnte nicht gesendet werden.', 'Feedback could not be sent.'));
      setSubmitted(true);
    } catch (err) {
      setError(err.message || ui('Feedback konnte nicht gesendet werden.', 'Feedback could not be sent.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) return <main className="page-shell feedback-page"><div className="brand-header"><p className="eyebrow">YourGermanAddress</p><h1>Feedback</h1></div><div className="card"><p className="status-description">{ui('Dieser Feedback-Link ist unvollständig. Bitte öffnen Sie den Link direkt aus Ihrer E-Mail.', 'This feedback link is incomplete. Please open it directly from your email.')}</p></div></main>;

  return (
    <main className="page-shell feedback-page">
      <div className="brand-header"><p className="eyebrow">YourGermanAddress</p><h1>{ui('Ihre ehrliche Rückmeldung', 'Your honest feedback')}</h1><p className="subtitle">{ui('Ihre Bewertung ist freiwillig. Jede Sternebewertung hat dieselben Möglichkeiten.', 'Your review is voluntary. Every star rating has the same options.')}</p></div>
      <div className="card feedback-card">
        <section className="feedback-private-section" aria-labelledby="feedback-heading">
          <h2 id="feedback-heading">{ui('Wie war Ihre Erfahrung?', 'How was your experience?')}</h2>
          <form className="feedback-form" onSubmit={handleSubmit}>
            <fieldset className="feedback-rating-fieldset"><legend>{ui('Bewertung auswählen', 'Select a rating')}</legend><div className="feedback-rating-buttons">{starButtons.map((value) => <button key={value} type="button" className={value <= rating ? 'is-selected' : ''} aria-label={ui(`${value} von 5 Sternen`, `${value} of 5 stars`)} aria-pressed={rating === value} onClick={() => setRating(value)}>★</button>)}</div></fieldset>
            <label>{ui('Private Nachricht an unser Team (optional)', 'Private message to our team (optional)')}<textarea value={message} onChange={(event) => setMessage(event.target.value)} maxLength={4000} placeholder={ui('Was lief gut oder was können wir verbessern?', 'What went well or what could we improve?')} /></label>
            {error ? <div className="hint-box" role="alert">{error}</div> : null}
            {submitted ? <div className="success-box" role="status">{ui('Vielen Dank. Ihre private Rückmeldung wurde gesendet.', 'Thank you. Your private feedback was sent.')}</div> : null}
            <button className="primary-button" type="submit" disabled={submitting || submitted}>{submitting ? ui('Wird gesendet...', 'Sending...') : ui('Private Rückmeldung senden', 'Send private feedback')}</button>
          </form>
        </section>
        <section className="feedback-public-section" aria-labelledby="public-review-heading">
          <h2 id="public-review-heading">{ui('Öffentlich bewerten', 'Leave a public review')}</h2>
          <p className="status-description">{ui('Unabhängig von Ihrer Sterneauswahl können Sie Ihre ehrliche Erfahrung öffentlich teilen.', 'Regardless of your star selection, you can share your honest experience publicly.')}</p>
          <div className="feedback-review-links"><a className="primary-link-button" href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer">{ui('Google bewerten', 'Review on Google')}</a><a className="primary-link-button" href={TRUSTPILOT_REVIEW_URL} target="_blank" rel="noreferrer">{ui('Trustpilot bewerten', 'Review on Trustpilot')}</a></div>
        </section>
      </div>
    </main>
  );
}

export default Feedback;
